/**
 * Easy Shipping Restrictions (restrictshipping.com) — ZIP gate for the theme cart drawer.
 *
 * Why this exists: the ESR app only gates checkout buttons that exist in the DOM
 * when its main.js runs at document ready (the /cart page button) plus the legacy
 * ReCharge shadow drawer. This theme's cart drawer is rendered after page load via
 * section AJAX (xHeaderMenu.renderAjax / xMiniCart.reLoad innerHTML swaps), so the
 * app never sees the drawer's checkout button and no ZIP check runs there.
 *
 * This script rebuilds the app's gate inside the drawer using the app's own
 * storefront contract (main.js v2026082701):
 *   - the ZIP-check click handler is delegated on document for
 *     ".restrict-shipping-check-button" and reads the zip from the sibling
 *     input ".restrict-shipping-check";
 *   - on allow, the app hides the check input/button and unlocks checkout via
 *     $("#checkout-submit").show().removeAttr("disabled") (store branch for
 *     jygj00-eh.myshopify.com);
 *   - on deny, it shakes the input and writes the server message into
 *     ".restrict-shipping-check-label".
 *
 * Fail-closed while the app is present: the drawer checkout button is hidden
 * until the app approves the ZIP. The theme never disables checkout itself.
 * After approval, shipping locks are reconciled against AgeChecker bootstrap
 * readiness, never label text or a remembered open/close sequence. If the shipping app is disabled, the drawer's own
 * gate is removed.
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '#x-cart-drawer-container';
  var CHECKOUT_SELECTOR = '#checkout-submit';
  var GATE_FLAG = 'data-mw-esr-gate';
  var HIDDEN_FLAG = 'data-mw-esr-hidden';
  var SESSION_KEY = 'mw_esr_drawer_passed';
  var ZIP_KEY = 'mw_esr_drawer_zip';
  var LABEL_TEXT = 'Check if we ship/deliver to your address';
  var passedInMemory = false;
  var activeGate = null;
  var activeGateObserver = null;


  function appScriptPresent() {
    return !!document.querySelector('script[src*="restrictshipping.com"]');
  }

  function sessionPassed() {
    if (passedInMemory) return true;
    try {
      passedInMemory = window.sessionStorage.getItem(SESSION_KEY) === '1';
      return passedInMemory;
    } catch (e) {
      return false;
    }
  }

  function markSessionPassed() {
    passedInMemory = true;
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
      /* Keep the approval in memory for this page when storage is unavailable. */
    }
  }

  function storedZip() {
    try {
      return window.localStorage.getItem(ZIP_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function rememberZip(value) {
    try {
      window.localStorage.setItem(ZIP_KEY, value);
    } catch (e) {
      /* non-fatal */
    }
  }

  var styleInjected = false;
  function ensureStyles() {
    if (styleInjected || document.getElementById('mw-esr-gate-style')) return;
    var style = document.createElement('style');
    style.id = 'mw-esr-gate-style';
    style.textContent = [
      '.mw-esr-gate { margin: 0 0 10px; width: 100% }',
      '.mw-esr-gate__label { text-align: left; display: block; margin: 0 0 8px; color: rgb(var(--colors-text-secondary, var(--colors-text))); font-family: var(--font-body-family); font-size: 11px; line-height: 1.35; }',
      '.mw-esr-gate__row { display: flex; gap: 8px; }',
      '.mw-esr-gate__row .restrict-shipping-check { flex: 1 1 auto; min-width: 0; padding: 10px 12px; border: 1px solid rgb(var(--cart-border, var(--colors-line-and-border))); border-radius: 10px; background: rgb(var(--colors-background)); color: rgb(var(--colors-text)); font-family: var(--font-body-family); font-size: 14px; outline: none; }',
      '.mw-esr-gate__row .restrict-shipping-check:focus { border-color: rgb(var(--cart-accent, 46, 125, 50)); }',
      '.mw-esr-gate__row .restrict-shipping-check-button { flex: 0 0 auto; min-height: 42px; padding: 8px 16px; border: 0; border-radius: 10px; background: rgb(var(--cart-accent, 46, 125, 50)); color: rgb(var(--cart-accent-contrast, 255, 255, 255)); font-family: var(--font-body-family); font-size: 14px; cursor: pointer; }',
      '.mw-esr-gate__row .restrict-shipping-check-button:hover { filter: brightness(1.08); }'
    ].join('\n');
    document.head.appendChild(style);
    styleInjected = true;
  }

  function buildGate() {
    var gate = document.createElement('div');
    gate.className = 'mw-esr-gate';
    gate.setAttribute(GATE_FLAG, '');

    var label = document.createElement('label');
    label.className = 'mw-esr-gate__label restrict-shipping-check-label';
    label.setAttribute('for', 'restrict-shipping-check-drawer');
    label.textContent = LABEL_TEXT;

    var row = document.createElement('div');
    row.className = 'mw-esr-gate__row';
    row.id = 'restrict-shipping-check-wrapper-drawer';

    var input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'text';
    input.autocomplete = 'postal-code';
    input.id = 'restrict-shipping-check-drawer';
    input.className = 'restrict-shipping-check';
    input.placeholder = 'Your ZIP/Postal Code';
    input.setAttribute('aria-label', 'Your ZIP/Postal Code');
    input.value = storedZip();
    input.addEventListener('input', function () {
      rememberZip(input.value.trim());
    });

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'restrict-shipping-check-button';
    button.textContent = 'Check';

    // The app binds Enter only on inputs present at document ready; mirror that
    // behavior for the dynamically injected input.
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        button.click();
      }
    });

    row.appendChild(input);
    row.appendChild(button);
    gate.appendChild(label);
    gate.appendChild(row);
    return gate;
  }

  // The unambiguous "zip verified" signal: the app's allow-path hides the check
  // input (inline display:none) before unlocking checkout. Watching the button's
  // disabled attribute instead is unsafe — the agechecker app toggles it too.
  function watchUnlock(gate) {
    if (activeGate === gate) return;
    if (activeGateObserver) activeGateObserver.disconnect();
    activeGate = gate;
    activeGateObserver = null;
    var input = gate.querySelector('.restrict-shipping-check');
    if (!input) return;
    if (input.style.display === 'none') {
      markSessionPassed();
      apply();
      return;
    }
    activeGateObserver = new MutationObserver(function () {
      if (input.style.display === 'none') {
        activeGateObserver.disconnect();
        activeGateObserver = null;
        markSessionPassed();
        apply();
      }
    });
    activeGateObserver.observe(input, { attributes: true, attributeFilter: ['style'] });
  }

  // Belt-and-suspenders: if something re-shows the checkout button while the
  // gate is armed and unverified, block the click and shake the input.
  function installBlocker(container) {
    if (container.dataset.mwEsrBlocker === '1') return;
    container.dataset.mwEsrBlocker = '1';
    function block(event) {
      if (sessionPassed() || !appScriptPresent()) return;
      if (!container.querySelector('[' + GATE_FLAG + ']')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      var input = container.querySelector('.restrict-shipping-check');
      if (input) {
        input.classList.remove('restrict-shipping-shake');
        void input.offsetWidth;
        input.classList.add('restrict-shipping-shake');
      }
    }
    container.addEventListener(
      'click',
      function (event) {
        var target = event.target && event.target.closest ? event.target : null;
        if (!target) return;
        var button = target.closest(CHECKOUT_SELECTOR);
        if (!button || !container.contains(button)) return;
        block(event);
      },
      true
    );
    container.addEventListener('submit', function (event) {
      var form = event.target && event.target.closest ? event.target.closest('#CartDrawer-Form') : null;
      if (form && container.contains(form)) block(event);
    }, true);
  }

  var scheduled = null;
  function scheduleApply(delay) {
    if (scheduled) window.clearTimeout(scheduled);
    scheduled = window.setTimeout(apply, delay === undefined ? 120 : delay);
  }

  function drawerOpen(container) {
    var drawer = container.querySelector('#CartDrawer');
    return !!(drawer && drawer.classList.contains('cart-open'));
  }

  // Mirrors the installed AgeChecker bootstrap: its marker means the click
  // handler is bound, NOT that the customer has passed age verification.
  function ageCheckerReady(button) {
    var enabled = !!(window.AgeChecker_APP_URL || window.AgeCheckerConfig);
    if (!enabled) return true;
    return !!(window.AgeCheckerUB || window.AgeChecker_Excluded ||
      String(button.className).indexOf('agechecker') !== -1);
  }

  function restoreApprovedCheckout(container, button, shippingApproved) {
    if (button.hasAttribute(HIDDEN_FLAG)) button.removeAttribute(HIDDEN_FLAG);
    if (drawerOpen(container) && button.style.display === 'none') button.style.display = '';
    if (shippingApproved && appScriptPresent() && button.disabled && ageCheckerReady(button)) {
      button.removeAttribute('disabled');
    }

    if (activeGateObserver) activeGateObserver.disconnect();
    activeGateObserver = null;
    activeGate = null;
    var stale = container.querySelector('[' + GATE_FLAG + ']');
    if (stale) stale.remove();
    // The app may also inject its native utility on reopen. Its form is hidden
    // by drawer CSS and must not linger beside an approved checkout button.
    var appUtility = container.querySelector('#restrict-shipping-utility');
    if (appUtility) appUtility.remove();
  }

  function apply() {
    var container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;
    // The cart page runs the app's native gate on its own checkout button.
    if (/\/(?:cart)\/?$/.test(window.location.pathname)) return;

    var button = container.querySelector(CHECKOUT_SELECTOR);
    if (!button) return; // empty cart — footer is not rendered

    installBlocker(container);

    var shippingApproved = sessionPassed();
    if (shippingApproved || !appScriptPresent()) {
      restoreApprovedCheckout(container, button, shippingApproved);
      return;
    }

    // The app's main.js injects its own unstyled "#restrict-shipping-utility"
    // form right before the dynamically added checkout button on first load.
    // We always remove it and render our own styled gate instead: validation
    // still runs on the app side because its click handler is delegated on
    // document for ".restrict-shipping-check-button" and reads the zip from
    // the sibling ".restrict-shipping-check" input — both provided by our
    // gate. The app does not re-inject the form after drawer re-renders, but
    // if it ever does, apply() removes it again (observer).
    var appUtility = button.parentNode.querySelector('#restrict-shipping-utility');
    if (appUtility) {
      var appInput = appUtility.querySelector('.restrict-shipping-check');
      if (appInput && appInput.style.display === 'none') {
        // The app allowed a zip in its own form before we could replace it —
        // adopt the pass and let the session-pass path keep checkout unlocked.
        markSessionPassed();
        appUtility.remove();
        restoreApprovedCheckout(container, button, true);
        return;
      }
      appUtility.remove();
    }

    ensureStyles();
    var gate = container.querySelector('[' + GATE_FLAG + ']');
    if (!gate) {
      gate = buildGate();
      button.parentNode.insertBefore(gate, button);
    }

    if (!button.hasAttribute(HIDDEN_FLAG)) button.setAttribute(HIDDEN_FLAG, '');
    if (button.style.display !== 'none') button.style.display = 'none';
    watchUnlock(gate);
  }

  function start() {
    // The cart page has the app's native gate and no drawer initialization is needed.
    if (/\/cart\/?$/.test(window.location.pathname)) return;

    function attachContainer() {
      var container = document.querySelector(CONTAINER_SELECTOR);
      if (!container) return false;
      new MutationObserver(function (mutations) {
        if (mutations.some(function (mutation) {
          return mutation.target.id === 'CartDrawer' && mutation.type === 'attributes';
        })) apply();
        if (mutations.some(function (mutation) {
          return mutation.type === 'attributes' &&
            (mutation.attributeName === 'style' || mutation.attributeName === 'disabled' || mutation.attributeName === 'class') &&
            mutation.target.id === 'checkout-submit';
        })) apply();
        else if (mutations.some(function (mutation) { return mutation.type === 'childList'; })) scheduleApply();
      }).observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'disabled']
      });
      document.addEventListener('mw:cart:rendered', apply);
      apply();
      return true;
    }

    if (!attachContainer()) {
      // Section AJAX can add the drawer long after DOM ready. Observe once
      // until it appears rather than polling forever on page-style carts.
      var containerObserver = new MutationObserver(function () {
        if (attachContainer()) containerObserver.disconnect();
      });
      containerObserver.observe(document.body, { childList: true, subtree: true });
    }
    // The shipping app can inject its script after this theme script starts.
    // Reconcile immediately without a recurring timer.
    new MutationObserver(function (mutations) {
      if (mutations.some(function (mutation) { return mutation.type === 'childList'; })) scheduleApply();
    }).observe(document.head, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
